import {
	ArrayPick,
	CardinalLocation,
	ClassNameProps,
	Column,
	DateTime,
	makeClassName,
	Modal,
	relativeTime,
	RelativeTimestamp,
	StyleProps,
	Tooltip,
} from "@hex-insights/core";
import { Icon } from "../../../../Components/Icon";
import { Announcement, User, UserNameAndImageFragment } from "../../../../Utilities";
import { UserUtilities } from "../../../Utilities";
import * as UserComponents from "../../User";
import { AnnouncementCard } from "../Card";
import { AnnouncementModal, AnnouncementModalProps } from "../Modal";
import styles from "./styles.module.css";

const tooltipSideOrder: CardinalLocation[] = ["right", "bottom", "left", "top"];

export type AnnouncementInIcon = Pick<Announcement, "id" | "title" | "body" | "startTime"> & {
	author: (Pick<User, "id"> & UserNameAndImageFragment) | null;
	mediaAttachments: ArrayPick<Announcement["mediaAttachments"], "isFile" | "fileContentType">;
};

export type AnnouncementIconProps = {
	announcement: AnnouncementInIcon & AnnouncementModalProps["announcement"];
	isNew?: boolean;
} & Partial<ClassNameProps & StyleProps>;

export function AnnouncementIcon({ announcement, isNew = false, className, style }: AnnouncementIconProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<div
			onClick={openModal}
			className={makeClassName(styles["announcement-icon"], isNew ? styles["announcement-icon--new"] : "", className)}
			style={style}
		>
			<Tooltip.Container sideOrder={tooltipSideOrder}>
				<Column justify="spaced-start" align="center" verticalSpacing="0.25rem">
					<div className={styles["announcement-icon__icon"]}>
						<div className={styles["announcement-icon__user-icon__container"]}>
							<UserComponents.UserIcon user={announcement.author ?? UserUtilities.placeholderUser} size={56} />
						</div>
						<div className={styles["announcement-icon__icon__sub-icon"]}>
							<Icon.Bell className={styles["announcement-icon__icon__sub-icon__icon"]} />
						</div>
					</div>
					<RelativeTimestamp
						value={announcement.startTime}
						format={formatTimestamp}
						className={styles["announcement-icon__timestamp"] + " nowrap"}
					/>
				</Column>

				<Tooltip>
					<Tooltip.Body className={styles["announcement-icon__tooltip__body"]}>
						<AnnouncementCard announcement={announcement} isNew={isNew} className={styles["announcement-icon__card"]} />
					</Tooltip.Body>
				</Tooltip>
			</Tooltip.Container>

			<Modal.If condition={isModalOpen}>
				<AnnouncementModal announcement={announcement} isNew={isNew} onClose={closeModal} />
			</Modal.If>
		</div>
	);
}

function formatTimestamp(value: DateTime) {
	const base = relativeTime(value);
	if (base === "a few seconds ago") {
		return "now";
	}
	return base
		.replace(/a month/, "1 month")
		.replace(/months?/, "mo")
		.replace(/minutes?/, "min");
}
